import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { MessageCircle, Send, CheckCircle2, Loader2, ChevronDown } from 'lucide-react';
import API from '../api/config';
import toast from 'react-hot-toast';

const CATEGORIES = [
  'General Enquiry',
  'Booking Issue',
  'Payment & Billing',
  'Technical Problem',
  'Expert Profile',
  'Account Access',
  'Session Recording',
  'Other',
];

const HelpPage = () => {
  const [form,      setForm]      = useState({ category: '', subject: '', message: '' });
  const [sending,   setSending]   = useState(false);
  const [sent,      setSent]      = useState(false);

  const upd = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSending(true);
    try {
      await API.post('/help', {
        subject:  form.subject.trim(),
        message:  form.message.trim(),
        category: form.category || 'General Enquiry',
      });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <DashboardLayout title="Help & Support">
        <div className="max-w-lg">
          <div className="bg-white border border-cream-200 rounded-2xl p-10 text-center space-y-4">
            <div className="w-16 h-16 bg-forest-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 size={32} className="text-forest-600" />
            </div>
            <h2 className="font-serif text-2xl font-semibold text-forest-900">Message sent!</h2>
            <p className="text-forest-500 text-sm leading-relaxed">
              Our team has received your message and will reply directly to your email address.
              You should hear back within 1 business day.
            </p>
            <p className="text-xs text-forest-400">A confirmation has been sent to your email.</p>
            <button
              onClick={() => { setSent(false); setForm({ category: '', subject: '', message: '' }); }}
              className="btn-primary mt-2 inline-flex"
            >
              Send another message
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Help & Support">
      <div className="max-w-lg space-y-6">

        {/* Info banner */}
        <div className="bg-forest-50 border border-forest-200 rounded-2xl px-5 py-4 flex items-start gap-3">
          <MessageCircle size={18} className="text-forest-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-forest-800 mb-0.5">We're here to help</p>
            <p className="text-xs text-forest-600 leading-relaxed">
              Send us a message and our support team will reply directly to your email address — usually within 1 business day.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white border border-cream-200 rounded-2xl p-6 space-y-5">
          <h3 className="font-serif text-lg font-semibold text-forest-900 border-b border-cream-100 pb-3">
            Contact Support
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-forest-700 mb-1.5">Category</label>
              <div className="relative">
                <select
                  value={form.category}
                  onChange={upd('category')}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white appearance-none pr-9"
                >
                  <option value="">Select a category...</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-forest-400 pointer-events-none" />
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-xs font-medium text-forest-700 mb-1.5">Subject *</label>
              <input
                type="text"
                value={form.subject}
                onChange={upd('subject')}
                placeholder="e.g. I can't access my session recording"
                required
                className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-xs font-medium text-forest-700 mb-1.5">Message *</label>
              <textarea
                value={form.message}
                onChange={upd('message')}
                rows={6}
                required
                placeholder="Describe your issue or question in detail..."
                className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white resize-none"
              />
              <p className="text-xs text-forest-400 mt-1">
                The more detail you provide, the faster we can help.
              </p>
            </div>

            <button
              type="submit"
              disabled={sending}
              className="btn-primary w-full justify-center flex items-center gap-2 disabled:opacity-60"
            >
              {sending
                ? <><Loader2 size={14} className="animate-spin" /> Sending...</>
                : <><Send size={14} /> Send Message</>}
            </button>
          </form>
        </div>

      </div>
    </DashboardLayout>
  );
};

export default HelpPage;